import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import '../styles/Header.scss';
import '../styles/search.scss';
import { supabase } from '@/lib/supabase';
import type { MusicData } from '@/types/music';
import { Calendar, History, ListMusic, Radio, RefreshCw, Search, Trophy, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ChangelogModal from './ChangelogModal.tsx';
import MonthlyCalendar from './MonthlyCalendar';
import SearchResults from './SearchResults';
import { Input } from './ui/input';

export interface HeaderProps {
  onDateChange: (date: string) => void;
  onSearchResultClick?: (title: string, artist: string) => void;
}

const Header: React.FC<HeaderProps> = ({ onDateChange, onSearchResultClick }) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [latestDate, setLatestDate] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<{
    artists: MusicData[];
    songs: MusicData[];
  }>({ artists: [], songs: [] });
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showChangelogModal, setShowChangelogModal] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const searchResultsRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  // Close search results and calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchResultsRef.current && !searchResultsRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setShowCalendar(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchLatestDate = async () => {
      try {
        // Check if we have a cached date and when it was last updated
        const cachedDate = localStorage.getItem('latestBroadcastDate');
        const lastUpdated = localStorage.getItem('latestDateFetched');
        const currentTime = new Date().getTime();

        // If we have a cached date and it was fetched less than 24 hours ago, use it
        if (
          cachedDate &&
          lastUpdated &&
          currentTime - Number.parseInt(lastUpdated) < 24 * 60 * 60 * 1000
        ) {
          setLatestDate(cachedDate);
          return;
        }

        // Otherwise fetch from the database
        const { data, error } = await supabase
          .from('music_data')
          .select('broadcast_date')
          .order('broadcast_date', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error fetching latest date:', error);
          return;
        }

        if (data && data.length > 0) {
          const latestDate = data[0].broadcast_date;
          setLatestDate(latestDate);

          // Cache the result
          localStorage.setItem('latestBroadcastDate', latestDate);
          localStorage.setItem('latestDateFetched', currentTime.toString());
        }
      } catch (error) {
        console.error('Failed to fetch latest date:', error);
      }
    };

    fetchLatestDate();
  }, []);

  // Load saved date from localStorage on initial render
  useEffect(() => {
    const savedDate = localStorage.getItem('selectedDate');
    if (savedDate) {
      setSelectedDate(savedDate);
      onDateChange(savedDate);
    }
  }, [onDateChange]);

  useEffect(() => {
    const search = async () => {
      if (!searchQuery.trim()) {
        setSearchResults({ artists: [], songs: [] });
        // 검색어가 비었을 때 결과 창을 숨김
        setShowResults(false);
        return;
      }

      setIsSearching(true);
      setShowResults(true); // 검색 시작 시 결과 창 표시
      try {
        const { data, error } = await supabase
          .from('music_data')
          .select('*')
          .or(`title.ilike.%${searchQuery}%,artist.ilike.%${searchQuery}%`)
          .limit(20);

        if (error) {
          console.error('Search error:', error);
          setSearchResults({ artists: [], songs: [] }); // 에러 시 결과 초기화
          return;
        }

        if (data) {
          // 중복 제거 및 결과 분류
          const uniqueArtists = new Map<string, MusicData>();
          const uniqueSongs = new Map<string, MusicData>();

          for (const item of data) {
            // 아티스트 이름이 검색어와 부분 일치하는 경우
            if (item.artist.toLowerCase().includes(searchQuery.toLowerCase())) {
              uniqueArtists.set(item.artist, item);
            }
            // 노래 제목이 검색어와 부분 일치하는 경우
            if (item.title.toLowerCase().includes(searchQuery.toLowerCase())) {
              uniqueSongs.set(`${item.title}-${item.artist}`, item);
            }
            // 아티스트 이름 + 노래 제목으로 검색하는 경우도 고려 (선택 사항)
            // if (`${item.artist} ${item.title}`.toLowerCase().includes(searchQuery.toLowerCase())) {
            //   uniqueSongs.set(`${item.title}-${item.artist}`, item);
            // }
          }

          setSearchResults({
            artists: Array.from(uniqueArtists.values()).slice(0, 5), // 아티스트 결과 5개로 제한
            songs: Array.from(uniqueSongs.values()).slice(0, 15), // 노래 결과 15개로 제한 (총 20개)
          });
        } else {
          setSearchResults({ artists: [], songs: [] }); // 데이터 없을 시 결과 초기화
        }
      } catch (error) {
        console.error('Search failed:', error);
        setSearchResults({ artists: [], songs: [] }); // 에러 시 결과 초기화
      } finally {
        setIsSearching(false);
      }
    };

    // 검색어가 있을 때만 디바운스 실행
    if (searchQuery.trim()) {
      const debounceTimer = setTimeout(search, 300);
      return () => clearTimeout(debounceTimer);
    }
    // 검색어가 없으면 즉시 결과 초기화 및 숨김
    setSearchResults({ artists: [], songs: [] });
    setShowResults(false);
  }, [searchQuery]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    // 입력할 때마다 결과 창을 보여줄 필요는 없음 -> useEffect에서 처리
  };

  const handleArtistClick = (artist: string) => {
    // 아티스트 클릭 시 해당 아티스트로 검색어 설정 및 결과 업데이트
    setSearchQuery(artist);
    // setShowResults(true); // 검색어가 변경되면 useEffect에서 자동으로 처리
  };

  const handleSongClick = (title: string, artist: string) => {
    if (onSearchResultClick) {
      onSearchResultClick(title, artist);
    }
    // 노래 클릭 시 검색어는 유지하고 결과 창만 닫기
    // setSearchQuery(''); // 검색어 유지
    setShowResults(false);
  };

  const handleCloseResults = () => {
    setShowResults(false);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    // setSearchResults({ artists: [], songs: [] }); // useEffect에서 처리됨
    // setShowResults(false); // useEffect에서 처리됨
  };

  // 검색 입력 필드에 포커스가 가면 결과 창 표시
  const handleSearchFocus = () => {
    if (
      searchQuery.trim() &&
      (searchResults.artists.length > 0 || searchResults.songs.length > 0)
    ) {
      setShowResults(true);
    }
  };

  // 검색 결과가 없고 검색어가 없을 때 showResults를 false로 설정
  // 검색 결과가 있거나 검색어가 있을 때는 검색 로직(useEffect)에서 showResults 관리
  // useEffect(() => {
  //   if (searchQuery.trim() && (searchResults.artists.length > 0 || searchResults.songs.length > 0)) {
  //     setShowResults(true);
  //   } else if (!searchQuery.trim()) {
  //     setShowResults(false);
  //   }
  // }, [searchResults, searchQuery]);

  const handleCalendarDateSelect = (date: string) => {
    setSelectedDate(date);
    localStorage.setItem('selectedDate', date);
    onDateChange(date);
    setShowCalendar(false);
  };

  return (
    <header className="header pb-2">
      {' '}
      {/* Reduced bottom margin from mb-8 to mb-6 */}
      <div className="container mx-auto max-w-7xl px-2 sm:px-4">
        <div className="flex flex-col">
          {/* Top header with logo, navigation and last updated info */}
          <div className="flex flex-col sm:flex-row justify-between items-center sm:items-center top-header gap-3 sm:gap-0 mb-3 sm:mb-0">
            <div className="flex items-center justify-center sm:justify-start gap-6 w-full sm:w-auto">
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <Radio size={18} className="text-blue-500" />
                  <div className="text-lg sm:text-base font-bold font-poppins tracking-tight text-white">
                    My Radio DB v0.8.6
                  </div>
                </div>
              </div>
            </div>

            <nav className="hidden sm:flex items-center space-x-6">
              <Link
                to="/"
                className={`text-base font-medium transition-colors hover:text-primary flex items-center gap-1.5 ${location.pathname === '/' ? 'text-primary' : 'text-gray-400'}`}
              >
                <ListMusic className="h-5 w-5" />
                Daily Setlist
              </Link>
              <Link
                to="/chronology"
                className={`text-base font-medium transition-colors hover:text-primary flex items-center gap-1.5 ${location.pathname === '/chronology' ? 'text-primary' : 'text-gray-400'}`}
              >
                <History className="h-5 w-5" />
                Chronology
              </Link>
              <Link
                to="/ranking"
                className={`text-base font-medium transition-colors hover:text-primary flex items-center gap-1.5 ${location.pathname === '/ranking' ? 'text-primary' : 'text-gray-400'}`}
              >
                <Trophy className="h-5 w-5" />
                Ranking
              </Link>
            </nav>

            <div className="flex items-center justify-center w-full sm:w-auto">
              <div className="text-xs">
                <button
                  type="button"
                  className="inline-flex items-center px-2 py-1 bg-gray-800 rounded-md border border-gray-700 hover:bg-gray-700 transition-colors cursor-pointer"
                  onClick={() => setShowChangelogModal(true)}
                  title="View changelog"
                >
                  <RefreshCw size={10} className="text-gray-400 mr-1.5" />
                  <span className="font-mono text-[9.4px] tracking-tighter text-gray-400">
                    {latestDate ? `Last Updated: ${latestDate}` : 'loading...'}
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Mobile navigation menu - Adjusted vertical margin to my-4 */}
          <div className="flex sm:hidden items-center justify-center w-full my-4">
            <nav className="flex items-center space-x-6">
              <Link
                to="/"
                className={`text-base font-medium transition-colors hover:text-primary flex items-center gap-1.5 ${location.pathname === '/' ? 'text-primary' : 'text-gray-400'}`}
              >
                <ListMusic className="h-5 w-5" />
                Daily Setlist
              </Link>
              <Link
                to="/chronology"
                className={`text-base font-medium transition-colors hover:text-primary flex items-center gap-1.5 ${location.pathname === '/chronology' ? 'text-primary' : 'text-gray-400'}`}
              >
                <History className="h-5 w-5" />
                Chronology
              </Link>
              <Link
                to="/ranking"
                className={`text-base font-medium transition-colors hover:text-primary flex items-center gap-1.5 ${location.pathname === '/ranking' ? 'text-primary' : 'text-gray-400'}`}
              >
                <Trophy className="h-5 w-5" />
                Ranking
              </Link>
            </nav>
          </div>

          {/* Search area - Common across all pages, larger size */}
          <div className="w-full max-w-2xl mx-auto relative z-40 mt-4 mb-4" ref={searchResultsRef}>
            <div className="flex items-center gap-2 w-full">
              {/* Calendar button - only visible on Daily Setlist page */}
              {location.pathname === '/' && (
                <button
                  type="button"
                  onClick={() => setShowCalendar(!showCalendar)}
                  className="flex-shrink-0 p-3 sm:p-4 bg-gray-900 hover:bg-gray-800 border border-gray-700 rounded-lg transition-colors"
                  aria-label="Open calendar"
                >
                  <Calendar size={20} className="text-gray-300" />
                </button>
              )}

              {/* Keyword search - always visible on all pages, larger size */}
              <div className="relative flex items-center flex-1">
                <Search
                  size={20}
                  className="absolute left-4 sm:left-5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
                <input
                  type="text"
                  placeholder="Search songs or artists..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={handleSearchFocus}
                  className="search-input w-full pl-12 sm:pl-14 pr-12 sm:pr-14 py-3 sm:py-4 h-12 sm:h-14 text-base sm:text-lg bg-gray-900 text-gray-200 placeholder-gray-500 outline-none rounded-lg focus:ring-2 focus:ring-inset focus:ring-blue-500 border border-gray-700"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 p-2 hover:bg-gray-700 rounded-full transition-colors"
                    aria-label="Clear search"
                  >
                    <X size={18} className="text-gray-400" />
                  </button>
                )}
              </div>
            </div>

            {/* Calendar dropdown - only visible on Daily Setlist page */}
            {location.pathname === '/' && showCalendar && (
              <div
                ref={calendarRef}
                className="absolute top-full left-0 mt-2 z-50 w-full sm:w-auto"
              >
                <MonthlyCalendar
                  selectedDate={selectedDate}
                  onDateSelect={handleCalendarDateSelect}
                  onClose={() => setShowCalendar(false)}
                />
              </div>
            )}

            {/* Search results - Positioned absolutely relative to search-group */}
            {showResults &&
              (searchResults.artists.length > 0 || searchResults.songs.length > 0) && (
                <div
                  className="absolute top-full left-0 right-0 mt-1 z-50 bg-gray-800 border border-gray-700 rounded-md shadow-lg overflow-hidden w-full"
                  style={{ maxHeight: '400px', overflowY: 'auto' }}
                >
                  <SearchResults
                    results={searchResults}
                    onArtistClick={handleArtistClick}
                    onSongClick={handleSongClick}
                    onClose={handleCloseResults}
                    isSearching={isSearching}
                  />
                </div>
              )}
          </div>
        </div>
      </div>
      <ChangelogModal isOpen={showChangelogModal} onClose={() => setShowChangelogModal(false)} />
    </header>
  );
};

export default Header;
